import React, { useRef, useState, useEffect } from 'react'
import {
  Container,
  Group,
  Text,
  Badge,
  Box,
  Modal,
  Anchor,
  Button,
  Title,
} from '@mantine/core'
import { useMutation } from '@apollo/client/react'
import { useLocation } from 'react-router-dom'
import { useAppDispatch } from '../AppContext'
import { Actions } from '../AppReducer'
import CurrentUser from '../graphql/queries/CurrentUser'
import CreateGuest from '../graphql/queries/CreateGuest'
import PublicRoomsFeed from './PublicRoomsFeed'
import { useModalStack } from '../ui/ModalStack'

// ─── Font constants ───────────────────────────────────────────────────────────
const CS   = "'Comic Sans MS', 'Chalkboard SE', cursive"
const IMP  = "Impact, Haettenschweiler, 'Arial Narrow Bold', sans-serif"
const MONO = "'Courier New', Courier, monospace"

// ─── Windows 98 style button ──────────────────────────────────────────────────
function W98Btn({ children, onClick, primary = false, big = false, style: extra = {} }) {
  const [pressed, setPressed] = useState(false)
  const up   = primary ? '2px solid #8080ff' : '2px solid #ffffff'
  const down = primary ? '2px solid #000040' : '2px solid #808080'
  return (
    <button
      onClick={onClick}
      onMouseDown={() => setPressed(true)}
      onMouseUp={() => setPressed(false)}
      onMouseLeave={() => setPressed(false)}
      style={{
        fontFamily: CS,
        fontSize: big ? 16 : 13,
        padding: big ? '10px 26px' : '4px 14px',
        background: primary ? '#000080' : '#c0c0c0',
        color: primary ? '#fff' : '#000',
        cursor: 'pointer',
        userSelect: 'none',
        whiteSpace: 'nowrap',
        borderTop:    pressed ? down : up,
        borderLeft:   pressed ? down : up,
        borderRight:  pressed ? up   : down,
        borderBottom: pressed ? up   : down,
        ...extra,
      }}
    >
      {children}
    </button>
  )
}

// ─── Thick divider ────────────────────────────────────────────────────────────
const ThickHr = () => <div style={{ borderTop: '4px solid #000', margin: 0 }} />

// ─── Section title ────────────────────────────────────────────────────────────
const SectionTitle = ({ children }) => (
  <div style={{
    fontFamily: CS, fontSize: 24, fontWeight: 'bold',
    textAlign: 'center', textDecoration: 'underline',
    marginBottom: 28, color: '#000',
  }}>
    {children}
  </div>
)

// ─── Character card ───────────────────────────────────────────────────────────
const CharCard = ({ title, subtitle, img, lines, bg = '#fff', accent = '#000' }) => (
  <div style={{
    border: `4px solid ${accent}`, background: bg,
    display: 'flex', flexDirection: 'column', alignItems: 'center',
    padding: '10px 10px 14px', width: 220, flexShrink: 0, fontFamily: CS,
  }}>
    <div style={{ fontFamily: IMP, fontSize: 22, letterSpacing: 0.5, marginBottom: 2, textAlign: 'center', color: accent }}>
      {title}
    </div>
    <div style={{ fontFamily: CS, fontSize: 11, marginBottom: 10, color: '#666', textAlign: 'center' }}>
      [{subtitle}]
    </div>
    <div style={{ border: '3px solid #000', overflow: 'hidden', lineHeight: 0, background: '#e8e8e8', width: 160, height: 170, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      {img}
    </div>
    <div style={{ marginTop: 12, fontSize: 12, width: '100%', lineHeight: 1.8, borderTop: '2px solid #000', paddingTop: 8 }}>
      {lines.map((ln, i) => <div key={i}>{ln}</div>)}
    </div>
  </div>
)

// ─── MSN-style chat window ────────────────────────────────────────────────────
const MsnChat = ({ messages }) => (
  <div style={{ border: '3px solid #000', overflow: 'hidden', maxWidth: 400 }}>
    <div style={{
      background: '#000080', color: '#fff', fontFamily: MONO,
      fontSize: 11, padding: '3px 8px',
      display: 'flex', justifyContent: 'space-between',
    }}>
      <span>🎵 Holmes – Conversation Window</span>
      <span>✕</span>
    </div>
    <div style={{ background: '#fff', padding: '8px 10px', fontFamily: MONO, fontSize: 12, minHeight: 180 }}>
      {messages.map(({ from, text, color }, i) => (
        <div key={i} style={{ marginBottom: 10, lineHeight: 1.5 }}>
          <span style={{ color, fontWeight: 'bold' }}>{from}: </span>
          <span style={{ color: '#000' }}>{text}</span>
        </div>
      ))}
    </div>
    <div style={{
      borderTop: '2px solid #808080', background: '#f0f0f0',
      padding: '4px 6px', display: 'flex', gap: 4,
    }}>
      <div style={{
        flex: 1, background: '#fff',
        borderTop: '2px solid #808080', borderLeft: '2px solid #808080',
        borderRight: '2px solid #fff', borderBottom: '2px solid #fff',
        padding: '2px 6px', fontFamily: MONO, fontSize: 11, color: '#aaa',
      }}>
        type here to ask holmes anything...
      </div>
      <W98Btn>Send</W98Btn>
    </div>
  </div>
)

// ─── Greentext block ──────────────────────────────────────────────────────────
const Greentext = ({ lines }) => (
  <div style={{
    fontFamily: MONO, fontSize: 13, lineHeight: 1.8,
    background: '#FFFFEE', border: '2px solid #000',
    padding: '12px 16px', maxWidth: 420,
    textAlign: 'left',
  }}>
    {lines.map((ln, i) => (
      <div key={i} style={{ fontWeight: 1000, color: ln.startsWith('>') ? '#789922' : '#000' }}>
        {ln}
      </div>
    ))}
  </div>
)

// ─── Inline SVG Wojak characters ──────────────────────────────────────────────

const WojakSad = ({ size = 130, headphones = false, hoodie = false }) => (
  <svg viewBox="0 0 120 155" width={size} height={size * (155 / 120)} style={{ display: 'block', margin: '0 auto' }}>
    {hoodie ? (
      <path d="M15 118 Q22 94 42 89 Q60 95 78 89 Q98 94 105 118 L115 155 L5 155 Z"
        fill="#1a1a1a" stroke="#000" strokeWidth="3" strokeLinejoin="round" />
    ) : (
      <path d="M42 96 Q60 106 78 96 L80 120 L40 120 Z"
        fill="#dec898" stroke="#000" strokeWidth="2" />
    )}
    {/* Head */}
    <ellipse cx="60" cy="60" rx="46" ry="52" fill="#dec898" stroke="#000" strokeWidth="3" />
    {/* Hair */}
    <path d="M14 48 Q22 8 60 6 Q98 8 106 48" fill="#c4a06c" stroke="#000" strokeWidth="2" />
    {/* Sad eyes */}
    <path d="M36 50 Q42 42 49 50" fill="none" stroke="#000" strokeWidth="3" strokeLinecap="round" />
    <path d="M71 50 Q78 42 84 50" fill="none" stroke="#000" strokeWidth="3" strokeLinecap="round" />
    <circle cx="42" cy="54" r="3" fill="#000" />
    <circle cx="78" cy="54" r="3" fill="#000" />
    {/* Nose */}
    <path d="M56 67 Q60 72 64 67" fill="none" stroke="#9a7050" strokeWidth="2" strokeLinecap="round" />
    {/* Sad mouth */}
    <path d="M43 82 Q60 74 77 82" fill="none" stroke="#000" strokeWidth="2.5" strokeLinecap="round" />
    {/* Tear */}
    <ellipse cx="42" cy="65" rx="3" ry="5" fill="#80c8e8" />
    {headphones && (
      <>
        <path d="M14 60 Q60 0 106 60" fill="none" stroke="#222" strokeWidth="6" strokeLinecap="round" />
        <rect x="4" y="53" width="16" height="20" rx="4" fill="#333" stroke="#111" strokeWidth="2.5" />
        <rect x="100" y="53" width="16" height="20" rx="4" fill="#333" stroke="#111" strokeWidth="2.5" />
      </>
    )}
  </svg>
)

const WojakNPC = ({ size = 130 }) => (
  <svg viewBox="0 0 120 155" width={size} height={size * (155 / 120)} style={{ display: 'block', margin: '0 auto' }}>
    <path d="M30 104 Q60 114 90 104 L94 155 L26 155 Z" fill="#999" stroke="#000" strokeWidth="2" />
    <ellipse cx="60" cy="60" rx="46" ry="52" fill="#aaa" stroke="#000" strokeWidth="3" />
    {/* Flat lines for eyes */}
    <line x1="37" y1="53" x2="52" y2="53" stroke="#000" strokeWidth="3.5" strokeLinecap="round" />
    <line x1="68" y1="53" x2="83" y2="53" stroke="#000" strokeWidth="3.5" strokeLinecap="round" />
    {/* Flat mouth */}
    <line x1="44" y1="78" x2="76" y2="78" stroke="#000" strokeWidth="2.5" strokeLinecap="round" />
    {/* [NPC] tag */}
    <rect x="36" y="106" width="48" height="18" fill="#fff" stroke="#000" strokeWidth="2" />
    <text x="60" y="119" textAnchor="middle" fontFamily="'Courier New', monospace" fontSize="11" fill="#000" fontWeight="bold">[NPC]</text>
  </svg>
)

const WojakChad = ({ size = 130 }) => (
  <svg viewBox="0 0 120 165" width={size} height={size * (165 / 120)} style={{ display: 'block', margin: '0 auto' }}>
    <path d="M20 120 Q60 108 100 120 L108 165 L12 165 Z" fill="#dec898" stroke="#000" strokeWidth="3" />
    {/* Large jaw */}
    <path d="M22 76 Q32 118 60 130 Q88 118 98 76" fill="#dec898" stroke="#000" strokeWidth="3" />
    <ellipse cx="60" cy="56" rx="42" ry="52" fill="#dec898" stroke="#000" strokeWidth="3" />
    <path d="M18 40 Q24 4 60 2 Q96 4 102 40" fill="#7a5a2a" stroke="#000" strokeWidth="2" />
    {/* Confident half-closed eyes */}
    <path d="M35 48 L50 48" stroke="#000" strokeWidth="3.5" strokeLinecap="round" />
    <path d="M70 48 L85 48" stroke="#000" strokeWidth="3.5" strokeLinecap="round" />
    <circle cx="42" cy="53" r="3.5" fill="#000" />
    <circle cx="77" cy="53" r="3.5" fill="#000" />
    {/* Smirk */}
    <path d="M46 76 Q62 86 80 72" fill="none" stroke="#000" strokeWidth="2.5" strokeLinecap="round" />
  </svg>
)

const WojakHappy = ({ size = 130 }) => (
  <svg viewBox="0 0 140 165" width={size} height={size * (165 / 140)} style={{ display: 'block', margin: '0 auto' }}>
    <path d="M25 115 Q70 103 115 115 L120 165 L20 165 Z" fill="#dec898" stroke="#000" strokeWidth="3" />
    <ellipse cx="70" cy="62" rx="46" ry="52" fill="#dec898" stroke="#000" strokeWidth="3" />
    <path d="M24 48 Q30 6 70 4 Q110 6 116 48" fill="#c4a06c" stroke="#000" strokeWidth="2" />
    {/* Happy upward-arc eyes */}
    <path d="M42 54 Q52 44 62 54" fill="none" stroke="#000" strokeWidth="3" strokeLinecap="round" />
    <path d="M78 54 Q88 44 98 54" fill="none" stroke="#000" strokeWidth="3" strokeLinecap="round" />
    {/* Blush */}
    <ellipse cx="36" cy="72" rx="11" ry="6" fill="rgba(255,130,130,0.45)" />
    <ellipse cx="104" cy="72" rx="11" ry="6" fill="rgba(255,130,130,0.45)" />
    {/* Big smile */}
    <path d="M44 82 Q70 100 96 82" fill="none" stroke="#000" strokeWidth="2.5" strokeLinecap="round" />
    <path d="M44 82 Q70 96 96 82 L96 86 Q70 103 44 86 Z" fill="#fff" stroke="#000" strokeWidth="1" />
    {/* Flowers */}
    <g transform="translate(2, 16)">
      {[0,60,120,180,240,300].map(a => (
        <ellipse key={a} cx={14 + 10 * Math.cos(a * Math.PI / 180)} cy={14 + 10 * Math.sin(a * Math.PI / 180)}
          rx="5" ry="5" fill="#ff88aa" stroke="#000" strokeWidth="1" />
      ))}
      <circle cx="14" cy="14" r="6" fill="#ffdd00" stroke="#000" strokeWidth="1.5" />
    </g>
    <g transform="translate(108, 10)">
      {[0,60,120,180,240,300].map(a => (
        <ellipse key={a} cx={14 + 10 * Math.cos(a * Math.PI / 180)} cy={14 + 10 * Math.sin(a * Math.PI / 180)}
          rx="5" ry="5" fill="#88aaff" stroke="#000" strokeWidth="1" />
      ))}
      <circle cx="14" cy="14" r="6" fill="#ffdd00" stroke="#000" strokeWidth="1.5" />
    </g>
  </svg>
)

const WojakRobot = ({ size = 130 }) => (
  <svg viewBox="0 0 120 165" width={size} height={size * (165 / 120)} style={{ display: 'block', margin: '0 auto' }}>
    {/* Body/torso */}
    <path d="M28 120 Q60 110 92 120 L100 165 L20 165 Z" fill="#888" stroke="#000" strokeWidth="3" />
    
    {/* Head - square robot face */}
    <rect x="24" y="36" width="72" height="88" fill="#999" stroke="#000" strokeWidth="3" rx="4" />
    
    {/* Antenna */}
    <line x1="60" y1="36" x2="60" y2="8" stroke="#000" strokeWidth="3" strokeLinecap="round" />
    <circle cx="60" cy="4" r="4" fill="#ff6b6b" stroke="#000" strokeWidth="2" />
    
    {/* Left digital eye */}
    <g>
      <rect x="34" y="50" width="18" height="18" fill="#00ff00" stroke="#000" strokeWidth="2" rx="2" />
      <rect x="37" y="53" width="4" height="4" fill="#000" />
      <rect x="43" y="53" width="4" height="4" fill="#000" />
      <rect x="37" y="59" width="4" height="4" fill="#000" />
      <rect x="43" y="59" width="4" height="4" fill="#000" />
    </g>
    
    {/* Right digital eye */}
    <g>
      <rect x="68" y="50" width="18" height="18" fill="#00ff00" stroke="#000" strokeWidth="2" rx="2" />
      <rect x="71" y="53" width="4" height="4" fill="#000" />
      <rect x="77" y="53" width="4" height="4" fill="#000" />
      <rect x="71" y="59" width="4" height="4" fill="#000" />
      <rect x="77" y="59" width="4" height="4" fill="#000" />
    </g>
    
    {/* Mouth - digital line */}
    <line x1="38" y1="85" x2="82" y2="85" stroke="#000" strokeWidth="3" strokeLinecap="round" />
    
    {/* Circuit board lines */}
    <line x1="60" y1="75" x2="60" y2="95" stroke="#666" strokeWidth="1.5" strokeDasharray="3,2" />
    <line x1="40" y1="80" x2="80" y2="80" stroke="#666" strokeWidth="1.5" strokeDasharray="3,2" />
    
    {/* Corner accent dots */}
    <circle cx="28" cy="40" r="2.5" fill="#ff6b6b" />
    <circle cx="92" cy="40" r="2.5" fill="#ff6b6b" />
    <circle cx="28" cy="120" r="2.5" fill="#ff6b6b" />
    <circle cx="92" cy="120" r="2.5" fill="#ff6b6b" />
  </svg>
)

// ═════════════════════════════════════════════════════════════════════════════
// MAIN COMPONENT
// ═════════════════════════════════════════════════════════════════════════════
export default function LandingPage() {
  const roomsRef = useRef(null)
  const dispatch = useAppDispatch()
  const location = useLocation()
  const { pushModal } = useModalStack()
  const [joiningRoomId, setJoiningRoomId] = useState(null)
  const [errorOpen, setErrorOpen] = useState(false)
  const [errorText, setErrorText] = useState('')
  const [feedKey, setFeedKey] = useState(Date.now())

  // Override body bg to white for lo-fi aesthetic
  useEffect(() => {
    const prev = document.body.style.background
    document.body.style.background = '#ffffff'
    return () => { document.body.style.background = prev }
  }, [])

  // Scroll to rooms if navigated here from another page with that intent
  useEffect(() => {
    if (location.state?.scrollToRooms) {
      setTimeout(() => {
        roomsRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })
      }, 100)
    }
  }, [location.state])

  // Reset feed key on mount to ensure fresh data after logout
  useEffect(() => {
    setFeedKey(Date.now())
  }, [])

  const [createGuest, { loading: creating, reset }] = useMutation(CreateGuest, {
    fetchPolicy: 'no-cache',
    update: (cache, { data }) => {
      // Delay writing to cache to ensure tokens are in localStorage before any auth'd queries run
      setTimeout(() => {
        if (data?.createGuest?.user) {
          cache.writeQuery({
            query: CurrentUser,
            data: data.createGuest
          })
        }
      }, 10)
    }
  })

  const scrollToRooms = () => roomsRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })

  async function handleJoin(roomId) {
    if (creating) return
    setJoiningRoomId(roomId)
    try {
      const result = await createGuest({ variables: { roomId } })
      const auth = result?.data?.createGuest
      if (!auth?.accessToken || !auth?.user?.id) {
        throw new Error('Failed to create user. Please try again.')
      }
      // Store tokens like a normal login
      localStorage.setItem('at', auth.accessToken)
      if (auth.refreshToken) localStorage.setItem('rt', auth.refreshToken)
      reset?.()
      // Remember room and mark logged in
      dispatch(Actions.setCurrentRoom(roomId))
      dispatch(Actions.logIn())
    } catch (e) {
      const msg = e?.networkError?.message || e?.message || 'An unknown error occurred.'
      setErrorText(msg)
      setErrorOpen(true)
      setJoiningRoomId(null)
      reset?.()
    }
  }

  // ─── Render ───────────────────────────────────────────────────────────────
  return (
    <Box style={{ background: '#fff', color: '#000', minHeight: '100vh', fontFamily: CS }}>

      {/* Error modal */}
      <Modal opened={errorOpen} onClose={() => setErrorOpen(false)} title="Something went wrong" centered>
        <Text size="sm" style={{ marginBottom: 12 }}>{errorText}</Text>
        <Group justify="flex-end">
          <Button onClick={() => setErrorOpen(false)}>OK</Button>
        </Group>
      </Modal>

      {/* ═══ HEADER ═════════════════════════════════════════════════════════ */}
      <Box component="header" style={{
        position: 'sticky', top: 0, zIndex: 100,
        background: '#000',
        borderBottom: '1px solid rgba(255,255,255,0.12)',
      }}>
        <Container fluid size="lg" py="xs">
          <Group justify="space-between" align="center">
            <Group align="center" gap="xs">
              <img
                src={`${process.env.PUBLIC_URL}/tunistry-note-icon-white.svg`}
                alt="Tunistry"
                width={24} height={24}
                style={{ display: 'block' }}
              />
              <Title
                order={2}
                style={{
                  margin: 0,
                  fontSize: 'clamp(18px, 4vw, 24px)',
                  fontWeight: 700,
                  fontFamily: 'Cairo, Arial, sans-serif',
                  letterSpacing: 0.2,
                  color: '#fff',
                }}
              >
                Tunistry
              </Title>
            </Group>
            <Group gap="sm">
              <Button
                variant="subtle"
                color="gray"
                onClick={() => pushModal('emailLogin')}
                styles={{ root: { fontFamily: 'Cairo, Arial, sans-serif' } }}
              >
                Log In
              </Button>
              <Button
                variant="gradient"
                gradient={{ from: 'violet', to: 'pink', deg: 135 }}
                onClick={() => pushModal('emailCreateAccount')}
                styles={{ root: { fontFamily: 'Cairo, Arial, sans-serif' } }}
              >
                Sign Up
              </Button>
            </Group>
          </Group>
        </Container>
      </Box>

      {/* ═══ HERO ════════════════════════════════════════════════════════════ */}
      <Container size="lg" py={44}>
        <div style={{ textAlign: 'center', marginBottom: 28 }}>
          <div style={{ fontFamily: IMP, fontSize: 38, letterSpacing: 1, color: '#000' }}>
            CHOOSE YOUR DESTINY:
          </div>
        </div>

        <div style={{ display: 'flex', gap: 16, justifyContent: 'center', flexWrap: 'wrap', alignItems: 'flex-start' }}>

          {/* YOU card */}
          <CharCard
            title="YOU"
            subtitle="regular person"
            img={<WojakSad size={154} headphones />}
            lines={[
              '• wants to listen to music',
              '• does not want to think about it',
              '• spotify keeps playing the same songs',
              '• has suffered for years',
              '• suffers in silence',
            ]}
            bg="#fff"
            accent="#000"
          />

          {/* VS */}
          <div style={{
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontFamily: IMP, fontSize: 52, color: '#cc0000',
            padding: '60px 8px 0',
          }}>
            VS.
          </div>

          {/* HOLMES card */}
          <CharCard
            title="HOLMES"
            subtitle="your AI DJ"
            img={<WojakRobot size={154} />}
            lines={[
              '• actually listens',
              '• learns your taste',
              '• remembers you',
              '• plays good songs',
              '• does not play bad songs',
            ]}
            bg="#ffffcc"
            accent="#0000cc"
          />
        </div>

        <div style={{ textAlign: 'center', marginTop: 36, display: 'flex', justifyContent: 'center', gap: 14, flexWrap: 'wrap' }}>
          <W98Btn onClick={scrollToRooms} primary big>🎵 start listening (it's free)</W98Btn>
        </div>
      </Container>

      <ThickHr />

      {/* ═══ HOW IT WORKS ════════════════════════════════════════════════════ */}
      <Box style={{ background: '#f8f8f8', padding: '44px 16px' }}>
        <Container size="lg">
          <SectionTitle>how it works (it is not complicated)</SectionTitle>
          <div style={{ display: 'flex', gap: 0, justifyContent: 'center', flexWrap: 'wrap', alignItems: 'stretch' }}>

            {[
              {
                title: 'BEFORE:',
                Wojak: <WojakNPC size={130} />,
                lines: [
                  '"shuffle liked songs"',
                  'heard them all 500 times',
                  'feels nothing',
                  'this has been going on for years',
                ],
                bg: '#f0f0f0',
                border: '#000',
              },
              {
                title: 'YOU, TYPING:',
                Wojak: <WojakSad size={130} />,
                lines: [
                  '"play something cinematic',
                  'and melancholy, like late',
                  'radiohead but more ambient"',
                  '',
                  'holmes hears you',
                  'holmes understands',
                ],
                bg: '#fff',
                border: '#000',
              },
              {
                title: '3 HOURS LATER:',
                Wojak: <WojakHappy size={130} />,
                lines: [
                  'discovered 4 new artists',
                  'finally feels something',
                  'one of them is now',
                  'their favorite artist',
                  '(happy crying)',
                ],
                bg: '#ffffcc',
                border: '#000',
              },
            ].map(({ title, Wojak, lines, bg, border }, i) => (
              <div key={i} style={{
                border: `4px solid ${border}`,
                borderRight: i < 2 ? '0' : `4px solid ${border}`,
                background: bg,
                display: 'flex', flexDirection: 'column', alignItems: 'center',
                padding: '12px 10px 16px',
                flex: '1 1 200px', minWidth: 200, maxWidth: 260,
                fontFamily: CS,
              }}>
                <div style={{ fontFamily: IMP, fontSize: 20, marginBottom: 10, textAlign: 'center' }}>{title}</div>
                {Wojak}
                <div style={{ marginTop: 12, fontSize: 12, textAlign: 'center', lineHeight: 1.8 }}>
                  {lines.map((ln, j) => <div key={j}>{ln || '\u00a0'}</div>)}
                </div>
              </div>
            ))}
          </div>
        </Container>
      </Box>

      <ThickHr />

      {/* ═══ EXPANDING BRAIN ═════════════════════════════════════════════════ */}
      <Box style={{ background: '#fff', padding: '44px 16px' }}>
        <Container size="lg">
          <SectionTitle>the tunistry advantage (illustrated)</SectionTitle>
          {[
            {
              brain: '🧠',
              brainSize: 28,
              bg: '#f5f5f5',
              text: 'shuffling your liked songs (you have heard them all 500 times)',
              color: '#000',
            },
            {
              brain: '🧠🧠',
              brainSize: 34,
              bg: '#e5f5e5',
              text: 'letting an algorithm make you a playlist (algorithmically optimal. spiritually empty.)',
              color: '#000',
            },
            {
              brain: '🧠🧠🧠',
              brainSize: 40,
              bg: '#e0e5ff',
              text: 'telling holmes "play something melancholy but not mopey, atmospheric, a little cinematic" and he just does it',
              color: '#000033',
            },
            {
              brain: '🌌🧠🌌',
              brainSize: 52,
              bg: '#f5ddff',
              text: 'you have been in the room for 8 months. reacted to 600 tracks. tagged your favorites. holmes has mapped your entire musical soul. he knows which eras you love, your complicated relationship with pop, that one song for 2am. you are beyond playlists now. you have ascended.',
              color: '#440066',
            },
          ].map(({ brain, brainSize, bg, text, color }, i, arr) => (
            <div key={i} style={{
              display: 'flex', alignItems: 'center',
              background: bg, border: '3px solid #000',
              borderBottom: i < arr.length - 1 ? '0' : '3px solid #000',
              padding: '14px 16px', gap: 20, fontFamily: CS,
            }}>
              <div style={{ fontSize: brainSize, flexShrink: 0, minWidth: 90, textAlign: 'center', lineHeight: 1 }}>
                {brain}
              </div>
              <div style={{ fontSize: 13, color, lineHeight: 1.7 }}>{text}</div>
            </div>
          ))}
        </Container>
      </Box>

      <ThickHr />

      {/* ═══ ASK GIPPITY ANYTHING ════════════════════════════════════════════ */}
      <Box style={{ background: '#f8f8f8', padding: '44px 16px', borderBottom: '4px solid #000' }}>
        <Container size="lg">
          <SectionTitle>also you can just ask holmes anything</SectionTitle>
          <div style={{ display: 'flex', gap: 40, justifyContent: 'center', flexWrap: 'wrap', alignItems: 'flex-start' }}>

            <MsnChat messages={[
              { from: 'You', color: '#000080', text: 'who made this track?' },
              { from: 'Holmes', color: '#800000', text: '"Pyramid Song" by Radiohead, Amnesiac (2001). Piano recorded at Abbey Road. Thom Yorke wrote it after a dream about ancient Egypt.' },
              { from: 'You', color: '#000080', text: 'what should i listen to next if i love this?' },
              { from: 'Holmes', color: '#800000', text: "Thom Yorke's solo stuff – Anima especially. Jon Hopkins. Ólafur Arnalds for the same atmosphere. Nils Frahm for the piano textures." },
            ]} />

            <div style={{ fontFamily: CS, maxWidth: 300 }}>
              <div style={{ fontWeight: 'bold', fontSize: 16, marginBottom: 14, lineHeight: 1.5 }}>
                holmes is also a music expert.<br />he specifically knows about:
              </div>
              {[
                ['📀', 'who made the track'],
                ['📖', 'the story behind it'],
                ['🔗', 'how it connects to other artists'],
                ['🗺️', "where it fits in an artist's career"],
                ['💡', 'what you should listen to next'],
                ['😭', 'why it hits different at 2am'],
              ].map(([icon, label]) => (
                <div key={label} style={{ marginBottom: 10, fontSize: 13, display: 'flex', gap: 8, alignItems: 'flex-start' }}>
                  <span style={{ flexShrink: 0 }}>{icon}</span>
                  <span>{label}</span>
                </div>
              ))}
              <div style={{ marginTop: 12, fontSize: 11, color: '#666', fontStyle: 'italic', lineHeight: 1.6 }}>
                (he is very knowledgeable. it is one of his things.)
              </div>
            </div>
          </div>
        </Container>
      </Box>

      {/* ═══ BRING FRENS ═════════════════════════════════════════════════════ */}
      <Box style={{ background: '#fff', padding: '44px 16px' }}>
        <Container size="lg">
          <SectionTitle>you can also bring your friends</SectionTitle>
          <div style={{ display: 'flex', gap: 48, justifyContent: 'center', flexWrap: 'wrap', alignItems: 'center' }}>

            {/* Wojak group */}
            <div style={{ display: 'flex', gap: 0, border: '4px solid #000', background: '#f8f8f8' }}>
              {[
                { W: <WojakSad size={100} />, label: 'you' },
                { W: <WojakHappy size={100} />, label: 'ur friend' },
                { W: <WojakChad size={100} />, label: 'ur other friend' },
              ].map(({ W, label }, i, arr) => (
                <div key={label} style={{
                  textAlign: 'center', padding: '10px 14px 8px',
                  borderRight: i < arr.length - 1 ? '3px solid #000' : 'none',
                }}>
                  {W}
                  <div style={{ fontFamily: CS, fontSize: 11, marginTop: 4 }}>{label}</div>
                </div>
              ))}
            </div>

            {/* Checklist + greentext */}
            <div>
              <div style={{ fontFamily: IMP, fontSize: 26, marginBottom: 14 }}>BRING YOUR FRIENDS.</div>
              <div style={{ fontFamily: CS, fontSize: 13, lineHeight: 2.1, marginBottom: 20 }}>
                {[
                  '✅ rooms can have multiple people',
                  '✅ everyone chats with holmes together',
                  '✅ react and tag tracks as a group',
                  '✅ holmes learns what the room likes',
                  '✅ the room develops a shared taste over time',
                  '✅ it is genuinely nice',
                ].map((ln, i) => <div key={i}>{ln}</div>)}
              </div>
              <Greentext lines={[
                '>be you and 3 friends',
                '>start a tunistry room',
                '>tell holmes "play something we can all vibe to"',
                '>he plays the perfect song',
                '>everyone reacts',
                '>holmes now knows what your friend group likes',
                '>6 months later you have a room with an actual identity',
                '>rooms develop their own taste over time',
                '>it is very nice',
              ]} />
            </div>
          </div>
        </Container>
      </Box>

      <ThickHr />

      {/* ═══ CTA ══════════════════════════════════════════════════════════════ */}
      <Box style={{ background: '#ffff99', borderBottom: '4px solid #000', padding: '32px 16px', textAlign: 'center' }}>
        <Container size="md">
          <div style={{ fontFamily: IMP, fontSize: 32, marginBottom: 10, letterSpacing: 0.5 }}>
            OK. YOU GET IT.
          </div>
          <div style={{ fontFamily: CS, fontSize: 14, lineHeight: 2.1, marginBottom: 8 }}>
            <div>here are some rooms you can join RIGHT NOW</div>
            <div>it is free. no account required to listen and chat.</div>
            <div>just click a room and you're in.</div>
            <div style={{ color: '#888', fontSize: 12 }}>(you can make your own room after signing up)</div>
          </div>
        </Container>
      </Box>

      {/* ═══ ROOMS FEED ══════════════════════════════════════════════════════ */}
      <Box style={{ background: '#1A1B1E', fontFamily: 'Cairo, Arial, sans-serif', color: '#fff' }} ref={roomsRef}>
        <Container size="lg" py="xl">
          <Group justify="space-between" align="center" mb="md">
            <Title order={3} style={{ color: '#fff', fontFamily: 'Cairo, Arial, sans-serif' }}>
              🎵 Jump In Now
            </Title>
            <Badge variant="light" color="blue">Live</Badge>
          </Group>
          <PublicRoomsFeed key={feedKey} onSelectRoom={handleJoin} joiningRoomId={joiningRoomId} />
          <Text size="xs" c="dimmed" mt="sm" ta="center">
            By joining a room, you agree to our{' '}
            <Anchor size="xs" href="/terms" target="_blank" rel="noopener noreferrer">Terms of Service</Anchor>
            {' '}and{' '}
            <Anchor size="xs" href="/privacy" target="_blank" rel="noopener noreferrer">Privacy Policy</Anchor>.
          </Text>
        </Container>
      </Box>

      {/* ═══ FOOTER ══════════════════════════════════════════════════════════ */}
      <Box component="footer" style={{ borderTop: '1px solid rgba(255,255,255,0.1)', background: '#1A1B1E', padding: '10px 16px' }}>
        <Container size="lg">
          <div style={{ display: 'flex', justifyContent: 'center', gap: 32, flexWrap: 'wrap', fontFamily: 'Cairo, Arial, sans-serif', fontSize: 12, color: '#aaa' }}>
            <span>© {new Date().getFullYear()} Tunistry. All rights reserved.</span>
            <Anchor size="xs" href="/privacy" target="_blank" rel="noopener noreferrer" c="dimmed">
              Privacy Policy
            </Anchor>
            <Anchor size="xs" href="/terms" target="_blank" rel="noopener noreferrer" c="dimmed">
              Terms of Service
            </Anchor>
          </div>
        </Container>
      </Box>

    </Box>
  )
}
